import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Typography, Box, Grid, Paper, LinearProgress, Divider, ToggleButtonGroup, ToggleButton, Tooltip, IconButton, Button, useTheme, } from '@weave-mui/material';
import { formatNumber } from '../components/StatCard';
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Link, } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceArea, ComposedChart, Area, } from 'recharts';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../api/apiClient';
import { queryKeys } from '../query/queryClient';
import { usePermissions } from '../hooks/usePermissions';
const DATE_RANGE_OPTIONS = [
    { label: '7D', days: 7 },
    { label: '30D', days: 30 },
    { label: '60D', days: 60 },
    { label: '90D', days: 90 },
];
const HOURLY_DATE_RANGE_OPTIONS = [
    { label: '24H', days: 1 },
    { label: '48H', days: 2 },
    { label: '7D', days: 7 },
];
function formatHourLabel(hour) {
    const d = new Date(`${hour}:00:00Z`);
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        timeZone: 'UTC',
    });
}
function getDateRange(daysBack) {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - daysBack);
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    };
}
function formatOrdinalDate(date) {
    const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
    const day = date.getUTCDate();
    const suffix = day % 100 >= 11 && day % 100 <= 13
        ? 'th'
        : day % 10 === 1
            ? 'st'
            : day % 10 === 2
                ? 'nd'
                : day % 10 === 3
                    ? 'rd'
                    : 'th';
    return `${month} ${day}${suffix}`;
}
function escHtml(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
const EXPORT_CHART_SCRIPT = `
(function(){
  var SVG='http://www.w3.org/2000/svg';
  var tip=document.createElement('div');
  Object.assign(tip.style,{position:'fixed',display:'none',background:'#fff',border:'1px solid #e0e0e0',
    borderRadius:'8px',padding:'12px 14px',boxShadow:'0 4px 20px rgba(0,0,0,.14)',pointerEvents:'none',
    zIndex:'9999',minWidth:'160px',maxWidth:'220px',fontFamily:'-apple-system,BlinkMacSystemFont,sans-serif'});
  document.body.appendChild(tip);

  function fmtN(n){if(n>=1e9)return(n/1e9).toFixed(1)+'B';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1e3)return(n/1e3).toFixed(1)+'K';return String(n);}
  function svgEl(t,a){var e=document.createElementNS(SVG,t);Object.entries(a||{}).forEach(function(kv){e.setAttribute(kv[0],kv[1]);});return e;}

  function draw(cid,data,color,title,total,gran){
    var el=document.getElementById(cid);
    if(!el||!data.length)return;
    var W=el.clientWidth||960,H=280;
    var mx={top:15,right:20,bottom:56,left:64};
    var w=W-mx.left-mx.right,h=H-mx.top-mx.bottom;
    var max=Math.max.apply(null,data.map(function(d){return d.value;}));if(!max)max=1;
    var step=w/data.length,bw=Math.min(step*0.72,44);
    var svg=svgEl('svg',{width:W,height:H,viewBox:'0 0 '+W+' '+H});
    var g=svgEl('g',{transform:'translate('+mx.left+','+mx.top+')'});
    for(var i=0;i<=4;i++){
      var pct=i/4,yg=h*(1-pct);
      g.appendChild(svgEl('line',{x1:0,y1:yg,x2:w,y2:yg,stroke:'#eeeeee','stroke-width':1}));
      var yt=svgEl('text',{x:-7,y:yg+4,'text-anchor':'end',fill:'#bbb','font-size':'11','font-family':'sans-serif'});
      yt.textContent=fmtN(Math.round(max*pct));g.appendChild(yt);
    }
    var skip=Math.ceil(data.length/12);
    data.forEach(function(d,i){
      var bh=max>0?(d.value/max)*h:0;
      var x=i*step+(step-bw)/2,y=h-bh;
      var bar=svgEl('rect',{x:x,y:Math.max(y,0),width:bw,height:Math.max(bh,0),fill:color,rx:3,style:'cursor:pointer;transition:opacity .12s'});
      (function(d,bar){
        bar.addEventListener('mouseenter',function(ev){
          bar.style.opacity='0.72';
          var lbl=gran==='weekly'?'Week of '+d.label:d.label;
          var html='<div style="font-size:11px;color:#9e9e9e;margin-bottom:5px">'+lbl+'</div>';
          html+='<div style="font-size:12px;color:#757575;margin-bottom:2px">'+title+'</div>';
          html+='<div style="font-size:20px;font-weight:700;color:'+color+';line-height:1.2">'+d.value.toLocaleString()+'</div>';
          if(total>0)html+='<div style="font-size:11px;color:#bbb;margin-top:4px">'+Math.round(d.value/total*100)+'% of '+fmtN(total)+'</div>';
          tip.innerHTML=html;tip.style.display='block';
          tip.style.left=(ev.clientX+14)+'px';tip.style.top=(ev.clientY-75)+'px';
        });
        bar.addEventListener('mouseleave',function(){bar.style.opacity='1';tip.style.display='none';});
        bar.addEventListener('mousemove',function(ev){tip.style.left=(ev.clientX+14)+'px';tip.style.top=(ev.clientY-75)+'px';});
      })(d,bar);
      g.appendChild(bar);
      if(i%skip===0||i===data.length-1){
        var t=svgEl('text',{x:i*step+step/2,y:h+16,'text-anchor':'middle',fill:'#bbb','font-size':'10','font-family':'sans-serif'});
        t.textContent=d.label;g.appendChild(t);
      }
    });
    svg.appendChild(g);el.appendChild(svg);
  }

  var CHARTS=/*CHART_DATA_PLACEHOLDER*/[];
  CHARTS.forEach(function(c){draw(c.id,c.data,c.color,c.title,c.total||0,c.gran);});
  window.addEventListener('resize',function(){
    CHARTS.forEach(function(c){var el=document.getElementById(c.id);if(el)el.innerHTML='';});
    CHARTS.forEach(function(c){draw(c.id,c.data,c.color,c.title,c.total||0,c.gran);});
  });
})();`;
function generateExportHTML(generatedAt, settings, computed, charts) {
    const fmt = (n) => escHtml(n.toLocaleString());
    const fmtPct = (n) => escHtml(`${n}%`);
    const trend = (t) => t == null
        ? ''
        : `<span style="font-size:11px;font-weight:700;color:${t >= 0 ? '#4caf50' : '#f44336'};margin-left:6px">${t >= 0 ? '▲' : '▼'} ${escHtml(String(Math.abs(t)))}%</span>`;
    const progress = (pct, color) => `<div style="height:5px;background:#f0f0f0;border-radius:3px;margin-top:4px"><div style="height:100%;width:${Math.min(pct, 100)}%;background:${color};border-radius:3px"></div></div>`;
    const statRow = (label, value, extra = '', pct, progressColor) => `
    <div style="margin-bottom:10px">
      <div style="display:flex;justify-content:space-between;align-items:baseline">
        <span style="font-size:12px;color:#757575">${label}</span>
        <span style="font-size:14px;font-weight:700">${value}${pct !== undefined ? `<span style="font-size:12px;color:#9e9e9e;font-weight:400;margin-left:6px">(${fmtPct(pct)})</span>` : ''}${extra}</span>
      </div>
      ${pct !== undefined && progressColor ? progress(pct, progressColor) : ''}
    </div>`;
    const card = (title, accentColor, body) => `
    <div style="border:1px solid #e0e0e0;border-left:4px solid ${accentColor};border-radius:6px;padding:16px;flex:1;min-width:0">
      <div style="font-size:14px;font-weight:600;border-bottom:1px solid #f0f0f0;padding-bottom:8px;margin-bottom:12px">${title}</div>
      ${body}
    </div>`;
    const sectionHeader = (label) => `
    <div style="display:flex;align-items:center;gap:12px;margin:28px 0 14px">
      <span style="font-size:11px;font-weight:600;color:#9e9e9e;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap">${label}</span>
      <div style="flex:1;height:1px;background:#e0e0e0"></div>
    </div>`;
    const chartSection = charts
        .map((c) => `
    <div style="margin-bottom:32px">
      <div style="font-size:14px;font-weight:600;margin-bottom:12px">${escHtml(c.title)}</div>
      <div id="${escHtml(c.id)}" style="width:100%"></div>
    </div>`)
        .join('');
    const chartDataJson = JSON.stringify(charts.map((c) => ({
        id: c.id,
        title: c.title,
        color: c.color,
        data: c.data,
        total: c.total,
        gran: c.gran,
    })));
    const inlineScript = EXPORT_CHART_SCRIPT.replace('/*CHART_DATA_PLACEHOLDER*/[]', chartDataJson);
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Executive Dashboard – ${generatedAt}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fafafa;color:#212121;padding:32px;max-width:1100px;margin:0 auto}
  @media print{body{padding:0;background:#fff}}
</style>
</head>
<body>
<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #e0e0e0">
  <div>
    <div style="font-size:22px;font-weight:700">Executive Dashboard</div>
    <div style="font-size:12px;color:#9e9e9e;margin-top:4px">Generated ${generatedAt}</div>
  </div>
</div>

${sectionHeader('Overview')}
<div style="display:flex;gap:16px;flex-wrap:wrap">
  ${card('Total Commercial Hubs', '#9e9e9e', statRow('Hubs', fmt(settings.totalHubs)) + statRow('Snapshots', fmt(settings.totalSnapshots)))}
  ${card('Notification Done', '#4caf50', statRow('Hubs', fmt(computed.migratedHubs), trend(computed.hubsTrend), computed.migratedHubsPct, '#4caf50') +
        statRow('Snapshots', fmt(computed.migratedSnapshots), trend(computed.snapshotsTrend), computed.migratedSnapshotsPct, '#4caf50'))}
  ${card('CE Enabled', '#2196f3', statRow('Hubs', fmt(computed.ceEnabledHubs), '', computed.ceEnabledHubsPct, '#2196f3'))}
</div>

${sectionHeader('Migration Projection')}
<div style="display:flex;gap:16px;flex-wrap:wrap">
  ${card('Throughput', '#9e9e9e', statRow('Daily Hubs', fmt(settings.dailyThroughputHubs)) +
        statRow('Daily Snapshots', fmt(settings.dailyThroughputSnapshots)))}
  ${card('Next Week Projection', '#1976d2', statRow('Hubs', fmt(computed.projectedHubs), '', computed.projectedHubsPct, '#1976d2') +
        statRow('Snapshots', fmt(computed.projectedSnapshots), '', computed.projectedSnapshotsPct, '#1976d2'))}
  ${card('To-Go', '#ff9800', statRow('Hubs', fmt(computed.toGoHubs), '', computed.toGoHubsPct, '#ff9800') +
        statRow('Snapshots', fmt(computed.toGoSnapshots), '', computed.toGoSnapshotsPct, '#ff9800') +
        statRow('ETA', computed.etaDisplay, '', undefined, undefined))}
</div>

${sectionHeader('Trends')}
<div style="background:#fff;border:1px solid #e0e0e0;border-radius:6px;padding:20px">
  ${chartSection}
</div>

<script>${inlineScript}</script>
</body>
</html>`;
}
const OverviewColumn = ({ heading, tooltip, action, children }) => (_jsxs(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { lineHeight: 1, fontWeight: 600 }, children: heading }), tooltip && (_jsx(Tooltip, { title: tooltip, placement: "top", arrow: true, slotProps: {
                        tooltip: {
                            sx: {
                                fontSize: '0.8rem',
                                lineHeight: 1.6,
                                maxWidth: 280,
                                px: 1.5,
                                py: 1,
                                bgcolor: 'grey.800',
                                '& .MuiTooltip-arrow': { color: 'grey.800' },
                            },
                        },
                    }, children: _jsx(InfoOutlinedIcon, { sx: { fontSize: 12, color: 'text.disabled', cursor: 'help' } }) })), action && _jsx(Box, { sx: { ml: 'auto' }, children: action })] }), children] }));
const OverviewRow = ({ label, value, displayValue, pct, trend, progressColor, labelTooltip, muted }) => (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), labelTooltip && (_jsx(Tooltip, { title: labelTooltip, placement: "top", arrow: true, slotProps: {
                                tooltip: {
                                    sx: {
                                        fontSize: '0.8rem',
                                        lineHeight: 1.6,
                                        maxWidth: 280,
                                        px: 1.5,
                                        py: 1,
                                        bgcolor: 'grey.800',
                                        '& .MuiTooltip-arrow': { color: 'grey.800' },
                                    },
                                },
                            }, children: _jsx(InfoOutlinedIcon, { sx: { fontSize: 12, color: 'text.disabled', cursor: 'help' } }) }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }, children: [_jsx(Typography, { variant: "body1", sx: {
                                fontWeight: muted ? 'normal' : 'bold',
                                color: muted ? 'text.secondary' : 'text.primary',
                            }, children: displayValue ?? formatNumber(value) }), pct !== undefined && (_jsxs(Typography, { variant: "body2", color: "text.secondary", fontWeight: "bold", children: ["(", pct, "%)"] })), trend != null && (_jsxs(Box, { component: "span", sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                borderRadius: '20px',
                                px: 0.75,
                                py: 0.25,
                                bgcolor: '#f0fdf4',
                                color: '#16a34a',
                                fontSize: '10px',
                                fontWeight: 600,
                            }, children: [trend >= 0 ? '▲' : '▼', " ", Math.abs(trend), "%"] }))] })] }), pct !== undefined && progressColor && (_jsx(LinearProgress, { variant: "determinate", value: Math.min(pct, 100), color: progressColor, sx: (t) => ({
                mt: 0.75,
                height: 4,
                borderRadius: 2,
                backgroundColor: t.palette.divider,
                '& .MuiLinearProgress-bar': { borderRadius: 2 },
            }) }))] }));
const SectionHeader = ({ label, children, }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 2 }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { whiteSpace: 'nowrap' }, children: label }), _jsx(Divider, { sx: { flex: 1 } }), children] }));
const ChartTooltip = ({ active, payload, label, metricTitle, granularity, total, }) => {
    if (!active || !payload?.length)
        return null;
    const value = payload[0].value;
    const periodLabel = granularity === 'weekly' ? `Week of ${label}` : label;
    return (_jsxs(Paper, { elevation: 4, sx: (theme) => ({
            p: 1.75,
            minWidth: 170,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
        }), children: [_jsx(Typography, { variant: "caption", color: "text.disabled", display: "block", sx: { mb: 0.5 }, children: periodLabel }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.25 }, children: metricTitle }), _jsx(Typography, { variant: "h6", fontWeight: "bold", lineHeight: 1.2, children: value.toLocaleString() }), total !== undefined && total > 0 && (_jsxs(Typography, { variant: "caption", color: "text.disabled", display: "block", sx: { mt: 0.5 }, children: [Math.round((value / total) * 100), "% of total (", formatNumber(total), ")"] }))] }));
};
const MultiSeriesChartTooltip = ({ active, payload, label, granularity }) => {
    if (!active || !payload?.length)
        return null;
    const periodLabel = granularity === 'weekly' ? 'Week of' : 'Date';
    return (_jsxs(Paper, { sx: (theme) => ({
            p: 1.5,
            fontSize: 12,
            minWidth: 160,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
        }), children: [_jsxs(Typography, { variant: "caption", color: "text.disabled", display: "block", sx: { mb: 0.5 }, children: [periodLabel, ": ", label] }), payload.map((entry) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }, children: [_jsx(Box, { sx: { width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 } }), _jsxs(Typography, { variant: "body2", children: [entry.name, ": ", entry.value.toLocaleString()] })] }, entry.dataKey)))] }));
};
const TrendChart = ({ title, subtitle, data, series, color, lineChart, showAreaFill, granularity, total, onMouseDown, onMouseMove, onMouseUp, refAreaLeft, refAreaRight, }) => {
    const theme = useTheme();
    const isEmpty = data.every((d) => series ? series.every((s) => (d[s.key] ?? 0) === 0) : (d['value'] ?? 0) === 0);
    return (_jsxs(Paper, { sx: (theme) => ({
            p: 3,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
            '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
        }), children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600, mb: subtitle ? 0.5 : 2, color: 'text.primary' }, children: title }), subtitle && (_jsx(Typography, { variant: "caption", sx: { display: 'block', mb: 2, color: 'text.secondary', fontSize: '11px' }, children: subtitle })), isEmpty ? (_jsx(Box, { sx: { height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { variant: "body2", color: "text.disabled", children: "No migration data yet" }) })) : series ? (_jsx(ResponsiveContainer, { width: "100%", height: 200, style: { cursor: 'crosshair' }, children: _jsxs(LineChart, { data: data, margin: { top: 4, right: 8, left: 0, bottom: 20 }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false, stroke: theme.palette.divider }), _jsx(XAxis, { dataKey: "label", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: { stroke: theme.palette.divider } }), _jsx(YAxis, { tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: false, width: 42, tickFormatter: (v) => formatNumber(v) }), _jsx(RechartsTooltip, { content: _jsx(MultiSeriesChartTooltip, { total: total, granularity: granularity }) }), _jsx(Legend, { iconType: "circle", iconSize: 10, wrapperStyle: { fontSize: 12, color: theme.palette.text.secondary } }), series.map((s) => (_jsx(Line, { type: "monotone", dataKey: s.key, name: s.label, stroke: s.color, dot: false, strokeWidth: 2 }, s.key))), refAreaLeft && refAreaRight && (_jsx(ReferenceArea, { x1: refAreaLeft, x2: refAreaRight, fill: "#8884d8", fillOpacity: 0.2 }))] }) })) : lineChart ? (_jsx(ResponsiveContainer, { width: "100%", height: 200, style: { cursor: 'crosshair' }, children: showAreaFill ? (_jsxs(ComposedChart, { data: data, margin: { top: 4, right: 8, left: 0, bottom: 20 }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [_jsx("defs", { children: _jsxs("linearGradient", { id: "ceGradient", x1: "0", y1: "0", x2: "0", y2: "1", children: [_jsx("stop", { offset: "0%", stopColor: color, stopOpacity: 0.15 }), _jsx("stop", { offset: "100%", stopColor: color, stopOpacity: 0 })] }) }), _jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false, stroke: theme.palette.divider }), _jsx(XAxis, { dataKey: "label", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: { stroke: theme.palette.divider } }), _jsx(YAxis, { tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: false, width: 42, tickFormatter: (v) => formatNumber(v) }), _jsx(RechartsTooltip, { content: _jsx(ChartTooltip, { metricTitle: title, granularity: granularity, total: total }) }), refAreaLeft && refAreaRight && (_jsx(ReferenceArea, { x1: refAreaLeft, x2: refAreaRight, fill: "#8884d8", fillOpacity: 0.2 })), _jsx(Area, { type: "monotone", dataKey: "value", stroke: "none", fill: "url(#ceGradient)", fillOpacity: 1, isAnimationActive: false }), _jsx(Line, { type: "monotone", dataKey: "value", stroke: color, strokeWidth: 2, dot: false, connectNulls: true, isAnimationActive: false })] })) : (_jsxs(LineChart, { data: data, margin: { top: 4, right: 8, left: 0, bottom: 20 }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false, stroke: theme.palette.divider }), _jsx(XAxis, { dataKey: "label", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: { stroke: theme.palette.divider } }), _jsx(YAxis, { tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: false, width: 42, tickFormatter: (v) => formatNumber(v) }), _jsx(RechartsTooltip, { content: _jsx(ChartTooltip, { metricTitle: title, granularity: granularity, total: total }) }), _jsx(Line, { type: "monotone", dataKey: "value", stroke: color, dot: false, strokeWidth: 2, connectNulls: true }), refAreaLeft && refAreaRight && (_jsx(ReferenceArea, { x1: refAreaLeft, x2: refAreaRight, fill: "#8884d8", fillOpacity: 0.2 }))] })) })) : (_jsx(ResponsiveContainer, { width: "100%", height: 200, style: { cursor: 'crosshair' }, children: _jsxs(BarChart, { data: data, margin: { top: 5, right: 20, left: 0, bottom: 20 }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false, stroke: theme.palette.divider }), _jsx(XAxis, { dataKey: "label", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: { stroke: theme.palette.divider } }), _jsx(YAxis, { tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: false, width: 42, tickFormatter: (v) => formatNumber(v) }), _jsx(RechartsTooltip, { content: _jsx(ChartTooltip, { metricTitle: title, granularity: granularity, total: total }), cursor: { fill: 'rgba(0,0,0,0.04)' } }), _jsx(Bar, { dataKey: "value", fill: color, radius: [3, 3, 0, 0], maxBarSize: 36 }), refAreaLeft && refAreaRight && (_jsx(ReferenceArea, { x1: refAreaLeft, x2: refAreaRight, fill: "#8884d8", fillOpacity: 0.2 }))] }) }))] }));
};
const SuccessFailedTooltip = ({ active, payload, label, granularity, successColor, failedColor, }) => {
    if (!active || !payload?.length)
        return null;
    const success = payload.find((p) => p.dataKey === 'success')?.value ?? 0;
    const failed = payload.find((p) => p.dataKey === 'failed')?.value ?? 0;
    const total = success + failed;
    const successRate = total > 0 ? ((success / total) * 100).toFixed(1) : null;
    const periodLabel = granularity === 'weekly' ? 'Week of' : 'Date';
    return (_jsxs(Paper, { sx: (theme) => ({
            p: 1.5,
            fontSize: 12,
            minWidth: 140,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
        }), children: [_jsxs(Typography, { variant: "caption", color: "text.disabled", display: "block", sx: { mb: 0.5 }, children: [periodLabel, ": ", label] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }, children: [_jsx(Box, { sx: { width: 10, height: 10, borderRadius: '50%', bgcolor: successColor, flexShrink: 0 } }), _jsxs(Typography, { variant: "body2", children: ["Success: ", success.toLocaleString()] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Box, { sx: { width: 10, height: 10, borderRadius: '50%', bgcolor: failedColor, flexShrink: 0 } }), _jsxs(Typography, { variant: "body2", children: ["Failed: ", failed.toLocaleString()] })] }), successRate !== null && (_jsxs(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 }, children: ["Success rate: ", successRate, "%"] }))] }));
};
const SuccessFailedTrendChart = ({ title, data, granularity, successColor, failedColor, onMouseDown, onMouseMove, onMouseUp, refAreaLeft, refAreaRight, showSuccessRate = true, }) => {
    const theme = useTheme();
    const hasData = data.some((d) => d.success > 0 || d.failed > 0);
    const enrichedData = data.map((d) => ({
        ...d,
        successRate: d.success + d.failed > 0
            ? parseFloat(((d.success / (d.success + d.failed)) * 100).toFixed(1))
            : null,
    }));
    return (_jsxs(Paper, { sx: (theme) => ({
            p: 3,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
            '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
        }), children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600, mb: 2, color: 'text.primary' }, children: title }), !hasData ? (_jsx(Box, { sx: { height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { variant: "body2", color: "text.disabled", children: "No migration data yet" }) })) : (_jsx(ResponsiveContainer, { width: "100%", height: 200, style: { cursor: 'crosshair' }, children: _jsxs(BarChart, { data: enrichedData, margin: { top: 5, right: showSuccessRate ? 40 : 20, left: 0, bottom: 20 }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false, stroke: theme.palette.divider }), _jsx(XAxis, { dataKey: "label", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: { stroke: theme.palette.divider } }), _jsx(YAxis, { yAxisId: "count", tick: { fontSize: 10, fill: theme.palette.text.secondary }, tickLine: false, axisLine: false, width: 42, tickFormatter: (v) => formatNumber(v) }), showSuccessRate && _jsx(YAxis, { yAxisId: "rate", orientation: "right", domain: [0, 100], hide: true }), _jsx(RechartsTooltip, { content: _jsx(SuccessFailedTooltip, { granularity: granularity, successColor: successColor, failedColor: failedColor }), cursor: { fill: 'rgba(0,0,0,0.04)' } }), _jsx(Legend, { iconType: "circle", iconSize: 10, wrapperStyle: { fontSize: 12, color: theme.palette.text.secondary } }), _jsx(Bar, { yAxisId: "count", dataKey: "success", name: "Success", stackId: "a", fill: successColor, radius: [0, 0, 0, 0], maxBarSize: 36 }), _jsx(Bar, { yAxisId: "count", dataKey: "failed", name: "Failed", stackId: "a", fill: failedColor, radius: [3, 3, 0, 0], maxBarSize: 36 }), showSuccessRate && (_jsx(Line, { yAxisId: "rate", type: "monotone", dataKey: "successRate", name: "Success rate", stroke: theme.palette.text.secondary, strokeDasharray: "4 2", dot: false, strokeWidth: 1.5, connectNulls: true })), refAreaLeft && refAreaRight && (_jsx(ReferenceArea, { x1: refAreaLeft, x2: refAreaRight, fill: "#8884d8", fillOpacity: 0.2 }))] }) }))] }));
};
const Dashboard = () => {
    const theme = useTheme();
    const { canWrite } = usePermissions();
    const [granularity, setGranularity] = useState('daily');
    const [daysBack, setDaysBack] = useState(30);
    const [zoomIndices, setZoomIndices] = useState([0, -1]);
    const [refAreaLeft, setRefAreaLeft] = useState('');
    const [refAreaRight, setRefAreaRight] = useState('');
    const [isSelecting, setIsSelecting] = useState(false);
    const [ceMenuAnchor, setCeMenuAnchor] = useState(null);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const [printTimestamp, setPrintTimestamp] = useState('');
    const [syncStatsDialogOpen, setSyncStatsDialogOpen] = useState(false);
    const [syncStatsJobId, setSyncStatsJobId] = useState(null);
    const { startDate, endDate } = getDateRange(daysBack);
    const { data: settings, isLoading: settingsLoading } = useQuery({
        queryKey: queryKeys.settings.detail(),
        queryFn: () => api.settings.get(),
    });
    const { data: metricsData, isLoading: metricsLoading } = useQuery({
        queryKey: queryKeys.metrics.range(startDate, endDate, granularity),
        queryFn: () => api.metrics.get(startDate, endDate, granularity),
    });
    const syncCEMutation = useMutation({
        mutationFn: () => api.jobs.create({ batchId: 'GLOBAL', jobType: 'syncCEStatus' }),
    });
    const syncStatsMutation = useMutation({
        mutationFn: () => api.jobs.create({ batchId: 'GLOBAL', jobType: 'syncMigrationStats' }),
        onSuccess: (data) => {
            setSyncStatsJobId(data?.jobId ?? null);
        },
        onError: () => {
            setSyncStatsDialogOpen(false);
        },
    });
    const isLoading = settingsLoading || metricsLoading;
    const m = metricsData;
    const daily = m?.daily ?? [];
    const weekly = m?.weekly ?? [];
    const hourly = m?.hourly ?? [];
    const activeData = granularity === 'daily'
        ? daily.map((d) => ({ label: d.date, value: 0 }))
        : granularity === 'weekly'
            ? weekly.map((w) => ({ label: w.weekStart, value: 0 }))
            : hourly.map((h) => ({ label: formatHourLabel(h.hour), value: 0 }));
    const maxIdx = Math.max(0, activeData.length - 1);
    const z0 = Math.min(zoomIndices[0], maxIdx);
    const z1 = zoomIndices[1] < 0 ? maxIdx : Math.min(zoomIndices[1], maxIdx);
    const handleChartMouseDown = (e) => {
        if (!e.activeLabel)
            return;
        setRefAreaLeft(e.activeLabel);
        setRefAreaRight('');
        setIsSelecting(true);
    };
    const handleChartMouseMove = (e) => {
        if (!isSelecting || !e.activeLabel)
            return;
        setRefAreaRight(e.activeLabel);
    };
    const handleChartMouseUp = () => {
        if (!isSelecting)
            return;
        setIsSelecting(false);
        if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
            setRefAreaLeft('');
            setRefAreaRight('');
            return;
        }
        const leftIdx = activeData.findIndex((d) => d.label === refAreaLeft);
        const rightIdx = activeData.findIndex((d) => d.label === refAreaRight);
        if (leftIdx === -1 || rightIdx === -1) {
            setRefAreaLeft('');
            setRefAreaRight('');
            return;
        }
        const start = Math.min(leftIdx, rightIdx);
        const end = Math.max(leftIdx, rightIdx);
        setZoomIndices([start, end]);
        setRefAreaLeft('');
        setRefAreaRight('');
    };
    if (isLoading) {
        return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 3 }, children: [_jsx(DashboardIcon, { sx: { fontSize: 20, color: 'text.secondary' } }), _jsx(Typography, { variant: "h6", sx: { fontWeight: 600 }, children: "Executive Dashboard" })] }), _jsx(LinearProgress, {})] }));
    }
    const s = settings;
    const totalHubs = s?.totalCommercialHubs ?? 0;
    const totalSnapshots = s?.totalCommercialHubSnapshots ?? 0;
    const previousMigratedHubs = s?.previousMigratedHubs ?? 0;
    const previousMigratedSnapshots = s?.previousMigratedSnapshots ?? 0;
    const previousCeEnabledHubs = s?.previousCeEnabledHubs ?? 0;
    const dailyThroughputHubs = s?.dailyThroughputHubs ?? 0;
    const dailyThroughputSnapshots = s?.dailyThroughputSnapshots ?? 0;
    const totals = m?.totals ?? { hubsMigrated: 0, snapshotsMigrated: 0, ceEnabledHubs: 0 };
    const migratedHubs = previousMigratedHubs + totals.hubsMigrated;
    const migratedSnapshots = previousMigratedSnapshots + totals.snapshotsMigrated;
    const migratedHubsPct = totalHubs > 0 ? Math.round((migratedHubs / totalHubs) * 100) : 0;
    const migratedSnapshotsPct = totalSnapshots > 0 ? Math.round((migratedSnapshots / totalSnapshots) * 100) : 0;
    const last7 = daily.slice(-7);
    const prev7 = daily.slice(-14, -7);
    const last7Hubs = last7.reduce((a, d) => a + d.hubsMigrated, 0);
    const prev7Hubs = prev7.reduce((a, d) => a + d.hubsMigrated, 0);
    const last7Snapshots = last7.reduce((a, d) => a + d.snapshotsMigrated, 0);
    const prev7Snapshots = prev7.reduce((a, d) => a + d.snapshotsMigrated, 0);
    const hubsTrend = prev7Hubs > 0 ? Math.round(((last7Hubs - prev7Hubs) / prev7Hubs) * 100) : null;
    const snapshotsTrend = prev7Snapshots > 0
        ? Math.round(((last7Snapshots - prev7Snapshots) / prev7Snapshots) * 100)
        : null;
    const toGoHubs = Math.max(0, totalHubs - migratedHubs);
    const toGoSnapshots = Math.max(0, totalSnapshots - migratedSnapshots);
    const toGoHubsPct = totalHubs > 0 ? Math.round((toGoHubs / totalHubs) * 100) : 0;
    const toGoSnapshotsPct = totalSnapshots > 0 ? Math.round((toGoSnapshots / totalSnapshots) * 100) : 0;
    const ceEnabledHubs = previousCeEnabledHubs + (totals.ceEnabledHubs ?? 0);
    const ceEnabledHubsPct = totalHubs > 0 ? Math.round((ceEnabledHubs / totalHubs) * 100) : 0;
    const projectedHubs = dailyThroughputHubs * 7;
    const projectedSnapshots = dailyThroughputSnapshots * 7;
    const projectedHubsPct = totalHubs > 0 ? Math.round((projectedHubs / totalHubs) * 100) : 0;
    const projectedSnapshotsPct = totalSnapshots > 0 ? Math.round((projectedSnapshots / totalSnapshots) * 100) : 0;
    const etaDays = dailyThroughputSnapshots > 0 ? Math.ceil(toGoSnapshots / dailyThroughputSnapshots) : null;
    const etaDate = etaDays !== null ? new Date(Date.now() + etaDays * 24 * 60 * 60 * 1000) : null;
    const etaDisplay = etaDays !== null ? `${etaDays.toLocaleString()} Days (${formatOrdinalDate(etaDate)})` : 'N/A';
    const activeSource = granularity === 'daily' ? daily : granularity === 'weekly' ? weekly : hourly;
    const getLabel = (d) => {
        if (granularity === 'hourly' && d.hour)
            return formatHourLabel(d.hour);
        return d.date ?? d.weekStart ?? d.hour ?? '';
    };
    const allHubsData = activeSource.map((d) => ({
        label: getLabel(d),
        migrationSuccess: d.hubsMigrationSuccess ?? 0,
        validationSuccess: (d.hubsValidationSuccess ?? 0) + (d.hubsValidationSkipped ?? 0),
        userNotified: d.hubsMigrated,
    }));
    const allSnapshotsData = activeSource.map((d) => ({
        label: getLabel(d),
        migrationSuccess: d.snapshotsMigrationSuccess ?? 0,
        validationSuccess: (d.snapshotsValidationSuccess ?? 0) + (d.snapshotsValidationSkipped ?? 0),
        userNotified: d.snapshotsMigrated,
    }));
    const hubsSeries = [
        { key: 'migrationSuccess', label: 'Migration Succeeded', color: theme.palette.primary.main },
        {
            key: 'validationSuccess',
            label: 'Validation Passed',
            color: theme.palette.secondary.main,
        },
        { key: 'userNotified', label: 'Notification Done', color: theme.palette.success.main },
    ];
    const snapshotsSeries = [
        { key: 'migrationSuccess', label: 'Migration Succeeded', color: theme.palette.primary.main },
        { key: 'validationSuccess', label: 'Validation Passed', color: theme.palette.secondary.main },
        { key: 'userNotified', label: 'Notification Done', color: theme.palette.success.main },
    ];
    const allCeData = activeSource.map((d) => ({
        label: getLabel(d),
        value: d.ceEnabledHubs ?? 0,
    }));
    const allMigrationHubsData = activeSource.map((d) => ({
        label: getLabel(d),
        success: d.hubsMigrationSuccess ?? 0,
        failed: d.hubsMigrationFailed ?? 0,
    }));
    const allMigrationSnapshotsData = activeSource.map((d) => ({
        label: getLabel(d),
        success: d.snapshotsMigrationSuccess ?? 0,
        failed: d.snapshotsMigrationFailed ?? 0,
    }));
    const allValidationHubsData = activeSource.map((d) => ({
        label: getLabel(d),
        success: (d.hubsValidationSuccess ?? 0) + (d.hubsValidationSkipped ?? 0),
        failed: d.hubsValidationFailed ?? 0,
    }));
    const allValidationSnapshotsData = activeSource.map((d) => ({
        label: getLabel(d),
        success: (d.snapshotsValidationSuccess ?? 0) + (d.snapshotsValidationSkipped ?? 0),
        failed: d.snapshotsValidationFailed ?? 0,
    }));
    const hubsChartData = allHubsData.slice(z0, z1 + 1);
    const snapshotsChartData = allSnapshotsData.slice(z0, z1 + 1);
    const ceEnabledChartData = allCeData.slice(z0, z1 + 1);
    const migrationHubsChartData = allMigrationHubsData.slice(z0, z1 + 1);
    const migrationSnapshotsChartData = allMigrationSnapshotsData.slice(z0, z1 + 1);
    const validationHubsChartData = allValidationHubsData.slice(z0, z1 + 1);
    const validationSnapshotsChartData = allValidationSnapshotsData.slice(z0, z1 + 1);
    const handleExportHTML = () => {
        setExportMenuAnchor(null);
        try {
            const exportSettings = {
                totalHubs,
                totalSnapshots,
                prevMigratedHubs: previousMigratedHubs,
                prevMigratedSnapshots: previousMigratedSnapshots,
                prevCeEnabledHubs: previousCeEnabledHubs,
                dailyThroughputHubs,
                dailyThroughputSnapshots,
            };
            const exportComputed = {
                migratedHubs,
                migratedSnapshots,
                migratedHubsPct,
                migratedSnapshotsPct,
                ceEnabledHubs,
                ceEnabledHubsPct,
                toGoHubs,
                toGoSnapshots,
                toGoHubsPct,
                toGoSnapshotsPct,
                projectedHubs,
                projectedSnapshots,
                projectedHubsPct,
                projectedSnapshotsPct,
                etaDisplay,
                hubsTrend,
                snapshotsTrend,
            };
            const exportCharts = [
                {
                    id: 'chart-hubs',
                    title: 'Trend of Hubs',
                    color: theme.palette.success.main,
                    data: hubsChartData.map((d) => ({
                        label: d.label,
                        value: d.userNotified ?? 0,
                    })),
                    total: totalHubs,
                    gran: granularity,
                },
                {
                    id: 'chart-snapshots',
                    title: 'Trend of Snapshots',
                    color: theme.palette.secondary.main,
                    data: snapshotsChartData.map((d) => ({
                        label: d.label,
                        value: d.userNotified ?? 0,
                    })),
                    total: totalSnapshots,
                    gran: granularity,
                },
                {
                    id: 'chart-ce',
                    title: 'Trend of CE Enabled Hubs',
                    color: theme.palette.info.main,
                    data: ceEnabledChartData,
                    total: totalHubs,
                    gran: granularity,
                },
            ];
            const html = generateExportHTML(new Date().toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }), exportSettings, exportComputed, exportCharts);
            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10);
            const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
            link.download = `executive-dashboard-${dateStr}-${timeStr}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
        }
        catch (err) {
            console.error('Failed to export HTML', err);
        }
    };
    const handleExportPDF = () => {
        setExportMenuAnchor(null);
        setPrintTimestamp(new Date().toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }));
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
        const originalTitle = document.title;
        setTimeout(() => {
            try {
                document.title = `executive-dashboard-${dateStr}-${timeStr}`;
                window.print();
            }
            catch (err) {
                console.error('Failed to print', err);
            }
            finally {
                document.title = originalTitle;
                setPrintTimestamp('');
            }
        }, 100);
    };
    return (_jsxs(Box, { sx: { minHeight: '100vh' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }, children: [_jsx(DashboardIcon, { sx: { fontSize: 20, color: 'text.secondary' } }), _jsx(Typography, { variant: "h6", sx: { fontWeight: 600 }, children: "Executive Dashboard" }), printTimestamp && (_jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'none', displayPrint: 'block', ml: 1 }, children: ["Generated ", printTimestamp] })), _jsxs(Box, { sx: { ml: 'auto', displayPrint: 'none', display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Tooltip, { title: "Sync migration stats from collection records (last 14 days)", children: _jsx("span", { children: _jsx(IconButton, { size: "small", onClick: () => {
                                            setSyncStatsJobId(null);
                                            setSyncStatsDialogOpen(true);
                                        }, disabled: syncStatsMutation.isPending, children: _jsx(SyncIcon, { sx: {
                                                fontSize: 18,
                                                animation: syncStatsMutation.isPending ? 'spin 1s linear infinite' : 'none',
                                                '@keyframes spin': {
                                                    from: { transform: 'rotate(0deg)' },
                                                    to: { transform: 'rotate(360deg)' },
                                                },
                                            } }) }) }) }), _jsx(Button, { size: "small", variant: "outlined", color: "primary", startIcon: _jsx(FileDownloadIcon, {}), onClick: (e) => setExportMenuAnchor(e.currentTarget), children: "Export" }), _jsxs(Menu, { anchorEl: exportMenuAnchor, open: Boolean(exportMenuAnchor), onClose: () => setExportMenuAnchor(null), children: [_jsx(MenuItem, { onClick: handleExportPDF, children: "Export as PDF (Print)" }), _jsx(MenuItem, { onClick: handleExportHTML, children: "Export as HTML" })] })] })] }), _jsx(SectionHeader, { label: "Overview" }), _jsxs(Box, { sx: { display: 'flex', gap: 2, mb: 4 }, children: [_jsxs(Paper, { elevation: 0, sx: (t) => ({
                            flex: 1,
                            p: 3,
                            borderRadius: '12px',
                            border: `1px solid ${t.palette.divider}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            bgcolor: 'background.paper',
                            '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid', boxShadow: 'none' },
                        }), children: [_jsx(Typography, { variant: "subtitle1", sx: {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '12px',
                                    letterSpacing: '0.7px',
                                    color: 'text.primary',
                                    mb: 1.5,
                                }, children: "Latest Status" }), _jsx(Divider, { sx: { mb: 2 } }), _jsxs(Box, { sx: { display: 'flex', gap: 3 }, children: [_jsxs(OverviewColumn, { heading: "Total", tooltip: "The total number of commercial hubs to migrate. Update it in Settings.", children: [_jsx(OverviewRow, { label: "Hubs", value: totalHubs }), _jsx(OverviewRow, { label: "Snapshots", value: totalSnapshots })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsxs(OverviewColumn, { heading: "Notification Done", tooltip: "Hubs where hubDataVersion is set to 2.0.0 and user notifications have been sent.", children: [_jsx(OverviewRow, { label: "Hubs", value: migratedHubs, pct: migratedHubsPct, trend: hubsTrend, progressColor: "success" }), _jsx(OverviewRow, { label: "Snapshots", value: migratedSnapshots, pct: migratedSnapshotsPct, trend: snapshotsTrend, progressColor: "success" })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsx(OverviewColumn, { heading: "CE Enabled", tooltip: "Hubs that have opted into Collaboration Engine (CE). These are migrated via the CE enablement path.", action: canWrite ? (_jsxs(Box, { sx: { displayPrint: 'none' }, children: [_jsx(IconButton, { size: "small", onClick: (e) => setCeMenuAnchor(e.currentTarget), children: _jsx(MoreVertIcon, { sx: { fontSize: 16 } }) }), _jsx(Menu, { anchorEl: ceMenuAnchor, open: Boolean(ceMenuAnchor), onClose: () => setCeMenuAnchor(null), children: _jsx(MenuItem, { disabled: syncCEMutation.isPending, onClick: () => {
                                                            setCeMenuAnchor(null);
                                                            syncCEMutation.mutate();
                                                        }, children: syncCEMutation.isPending ? 'Syncing CE status…' : 'Sync CE status' }) })] })) : null, children: _jsx(OverviewRow, { label: "Hubs", value: ceEnabledHubs, pct: ceEnabledHubsPct, progressColor: "info" }) })] })] }), _jsxs(Paper, { elevation: 0, sx: (t) => ({
                            flex: 1,
                            p: 3,
                            borderRadius: '12px',
                            border: `1px solid ${t.palette.divider}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            bgcolor: 'background.paper',
                            '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid', boxShadow: 'none' },
                        }), children: [_jsx(Typography, { variant: "subtitle1", sx: {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '12px',
                                    letterSpacing: '0.7px',
                                    color: 'text.primary',
                                    mb: 1.5,
                                }, children: "Projection" }), _jsx(Divider, { sx: { mb: 2 } }), _jsxs(Box, { sx: { display: 'flex', gap: 3 }, children: [_jsxs(OverviewColumn, { heading: "Throughput", tooltip: "Configure daily throughput in Settings.", children: [_jsx(OverviewRow, { label: "Daily Hubs", value: dailyThroughputHubs }), _jsx(OverviewRow, { label: "Daily Snapshots", value: dailyThroughputSnapshots })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsxs(OverviewColumn, { heading: "Next Week", tooltip: "Projected hubs = Daily Throughput of Hubs \u00D7 7 days. Configure throughput in Settings.", children: [_jsx(OverviewRow, { label: "Hubs", value: projectedHubs, pct: projectedHubsPct, progressColor: "primary" }), _jsx(OverviewRow, { label: "Snapshots", value: projectedSnapshots, pct: projectedSnapshotsPct, progressColor: "primary" })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsxs(OverviewColumn, { heading: "To-Go", tooltip: "Total remaining commercial hubs yet to be migrated.", children: [_jsx(OverviewRow, { label: "Hubs", value: toGoHubs, pct: toGoHubsPct, progressColor: "warning" }), _jsx(OverviewRow, { label: "Snapshots", value: toGoSnapshots, pct: toGoSnapshotsPct, progressColor: "warning" }), _jsx(OverviewRow, { label: "ETA", value: etaDays ?? 0, displayValue: etaDisplay, muted: true, labelTooltip: "ETA = To-Go Snapshots \u00F7 Daily Throughput of Snapshots. Configure throughput in Settings." })] })] })] })] }), _jsxs(Box, { sx: { '@media print': { breakBefore: 'page', pageBreakBefore: 'always' } }, children: [_jsx(SectionHeader, { label: "Trends", children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, displayPrint: 'none' }, children: [_jsxs(ToggleButtonGroup, { value: granularity, exclusive: true, onChange: (_, v) => {
                                        if (v) {
                                            setGranularity(v);
                                            setZoomIndices([0, -1]);
                                            if (v === 'hourly' && daysBack > 7)
                                                setDaysBack(1);
                                        }
                                    }, size: "small", sx: {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        '& .MuiToggleButton-root': {
                                            border: 'none',
                                            color: '#64748b',
                                            bgcolor: '#fff',
                                            '&.Mui-selected': {
                                                bgcolor: '#0696d7',
                                                color: '#fff',
                                                '&:hover': { bgcolor: '#0284c7' },
                                            },
                                            '&:hover': { bgcolor: '#f8fafc' },
                                        },
                                    }, children: [_jsx(ToggleButton, { value: "hourly", children: "Hourly" }), _jsx(ToggleButton, { value: "daily", children: "Daily" }), _jsx(ToggleButton, { value: "weekly", children: "Weekly" })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), (granularity === 'hourly' ? HOURLY_DATE_RANGE_OPTIONS : DATE_RANGE_OPTIONS).map((opt) => (_jsx(Button, { size: "small", variant: "outlined", onClick: () => {
                                        setDaysBack(opt.days);
                                        setZoomIndices([0, -1]);
                                    }, sx: daysBack === opt.days
                                        ? {
                                            minWidth: 0,
                                            px: 1,
                                            py: 0.25,
                                            fontSize: '0.7rem',
                                            bgcolor: '#eff6ff',
                                            color: '#0696d7',
                                            borderColor: '#bfdbfe',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: '#eff6ff',
                                                boxShadow: 'none',
                                            },
                                        }
                                        : {
                                            minWidth: 0,
                                            px: 1,
                                            py: 0.25,
                                            fontSize: '0.7rem',
                                            bgcolor: '#fff',
                                            color: '#64748b',
                                            borderColor: '#e2e8f0',
                                            '&:hover': { bgcolor: '#f8fafc' },
                                        }, children: opt.label }, opt.label))), (z0 !== 0 || z1 !== maxIdx) && (_jsxs(_Fragment, { children: [_jsx(Divider, { orientation: "vertical", flexItem: true }), _jsx(Button, { size: "small", variant: "text", onClick: () => setZoomIndices([0, -1]), sx: { minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }, children: "Reset zoom" })] }))] }) }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(TrendChart, { title: "Trend of Hubs", subtitle: "Migration \u00B7 Validation \u00B7 Notification", data: hubsChartData, series: hubsSeries, granularity: granularity, total: totalHubs, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(TrendChart, { title: "Trend of Snapshots", subtitle: "Migration \u00B7 Validation \u00B7 Notification", data: snapshotsChartData, series: snapshotsSeries, granularity: granularity, total: totalSnapshots, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(SuccessFailedTrendChart, { title: "Hubs by Migration Status", data: migrationHubsChartData, granularity: granularity, successColor: theme.palette.primary.main, failedColor: theme.palette.warning.main, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(SuccessFailedTrendChart, { title: "Number of Snapshots in Migration Success/Failed Hubs", data: migrationSnapshotsChartData, granularity: granularity, successColor: theme.palette.primary.main, failedColor: theme.palette.warning.main, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(SuccessFailedTrendChart, { title: "Hubs by Validation Status", data: validationHubsChartData, granularity: granularity, successColor: theme.palette.primary.main, failedColor: theme.palette.warning.main, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, md: 6, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(SuccessFailedTrendChart, { title: "Number of Snapshots in Validation Success/Failed Hubs", data: validationSnapshotsChartData, granularity: granularity, successColor: theme.palette.primary.main, failedColor: theme.palette.warning.main, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) }), _jsx(Grid, { item: true, xs: 12, sx: { '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' } }, children: _jsx(TrendChart, { title: "Trend of CE Enabled Hubs", subtitle: "Cumulative adoption", data: ceEnabledChartData, color: theme.palette.info.main, lineChart: true, showAreaFill: true, granularity: granularity, total: totalHubs, onMouseDown: handleChartMouseDown, onMouseMove: handleChartMouseMove, onMouseUp: handleChartMouseUp, refAreaLeft: refAreaLeft, refAreaRight: refAreaRight }) })] })] }), _jsxs(Dialog, { open: syncStatsDialogOpen, onClose: () => setSyncStatsDialogOpen(false), maxWidth: "sm", fullWidth: true, children: [_jsxs(DialogTitle, { sx: { pr: 6 }, children: ["Sync Migration Stats", _jsx(IconButton, { onClick: () => setSyncStatsDialogOpen(false), sx: { position: 'absolute', right: 8, top: 8 }, children: _jsx(CloseIcon, {}) })] }), _jsx(DialogContent, { children: !syncStatsJobId ? (_jsx(Typography, { children: "This will start an async job to recompute migration statistics for the last 14 days from collection records. The job may take several minutes to complete." })) : (_jsxs(Box, { children: [_jsx(Typography, { gutterBottom: true, children: "Job started successfully." }), _jsxs(Typography, { children: ["Job ID:", ' ', _jsx(Link, { component: RouterLink, to: "/jobs?jobType=syncMigrationStats", onClick: () => setSyncStatsDialogOpen(false), children: syncStatsJobId })] })] })) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setSyncStatsDialogOpen(false), children: syncStatsJobId ? 'Close' : 'Cancel' }), !syncStatsJobId && (_jsx(Button, { variant: "contained", onClick: () => syncStatsMutation.mutate(), disabled: syncStatsMutation.isPending, children: syncStatsMutation.isPending ? 'Starting…' : 'Confirm' }))] })] })] }));
};
export default Dashboard;
